import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const inputCls = "w-full px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm font-mono-spec";

function EvalEditRow({ ev, index, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-2" data-testid={`dates-eval-row-${index}`}>
      <input
        type="text"
        value={ev.title}
        onChange={(e) => onChange({ ...ev, title: e.target.value })}
        className="flex-1 px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm"
      />
      <input
        type="date"
        value={ev.date}
        onChange={(e) => onChange({ ...ev, date: e.target.value })}
        className="w-40 px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm font-mono-spec"
      />
      <button className="btn-secondary !py-2 !px-2 !text-[var(--late)]" onClick={onRemove} title="Supprimer l'évaluation" data-testid={`dates-eval-delete-${index}`}>
        <Trash2 size={14} />
      </button>
    </div>
  );
}

function useChapterDatesEditor(chapter, onSaved) {
  const [started, setStarted] = useState((chapter.started_at || "").slice(0, 10));
  const [ended, setEnded] = useState((chapter.ended_at || "").slice(0, 10));
  const [evals, setEvals] = useState((chapter.evaluations || []).map((e) => ({ ...e, date: (e.date || "").slice(0, 10) })));
  const [busy, setBusy] = useState(false);

  const removeEval = async (evalId) => {
    try {
      await api.delete(`/admin/chapters/${chapter.id}/evaluations/${evalId}`);
      setEvals((list) => list.filter((e) => e.id !== evalId));
      toast.success("Évaluation supprimée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/chapters/${chapter.id}/dates`, { started_at: started, ended_at: ended });
      for (const ev of evals) {
        await api.put(`/admin/chapters/${chapter.id}/evaluations/${ev.id}`, { title: ev.title, date: ev.date });
      }
      toast.success("Progression réelle corrigée");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return { started, setStarted, ended, setEnded, evals, setEvals, busy, removeEval, save };
}

export default function ChapterDatesDialog({ chapter, onClose, onSaved }) {
  const { started, setStarted, ended, setEnded, evals, setEvals, busy, removeEval, save } = useChapterDatesEditor(chapter, onSaved);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Corriger la progression réelle</DialogTitle>
          <DialogDescription>
            Chapitre {chapter.order} · {chapter.title} — seule la direction peut modifier ces dates. Laissez vide pour effacer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eyebrow block mb-1">Date de début</label>
              <input type="date" value={started} onChange={(e) => setStarted(e.target.value)} className={inputCls} data-testid="dates-started-input" />
            </div>
            <div>
              <label className="eyebrow block mb-1">Date de fin</label>
              <input type="date" value={ended} onChange={(e) => setEnded(e.target.value)} className={inputCls} data-testid="dates-ended-input" />
            </div>
          </div>
          {evals.length > 0 && (
            <div>
              <label className="eyebrow block mb-2">Évaluations</label>
              <div className="space-y-2">
                {evals.map((ev, i) => (
                  <EvalEditRow
                    key={ev.id}
                    ev={ev}
                    index={i}
                    onChange={(next) => setEvals((l) => l.map((x) => (x.id === ev.id ? next : x)))}
                    onRemove={() => removeEval(ev.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={save} disabled={busy} data-testid="dates-save-btn">Enregistrer</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
